/**
 * The apps the hub signs people into, and the roles each one accepts.
 *
 * Each app's own `shared/enums.ts` stays the source of truth for its roles: it rejects a push
 * with a role it does not know, so a drift between this list and the app fails loudly (the hub
 * shows "not updated") instead of quietly granting the wrong thing. Keep the two in step.
 *
 * URLs are not here: they differ between this Mac and the internal host, so they come from the
 * server's env (QA_WEB_URL, QA_API_URL, ACADEMY_WEB_URL, ACADEMY_API_URL).
 */
import { z } from 'zod';

export const AppKey = z.enum(['qa', 'academy']);
export type AppKey = z.infer<typeof AppKey>;
export const APP_KEYS = AppKey.options;

export type AppRole = { value: string; label: string; hint: string };

export type AppDef = {
  key: AppKey;
  name: string;
  /** One line for the launcher card. */
  blurb: string;
  /** Most privileged first. */
  roles: AppRole[];
};

export const APPS: Record<AppKey, AppDef> = {
  qa: {
    key: 'qa',
    name: 'Quality Review',
    blurb: 'Score Gladly conversations, see your QA and the team’s.',
    roles: [
      { value: 'admin', label: 'Admin', hint: 'Everything, plus Gladly linking, teams and integration settings' },
      { value: 'qa_manager', label: 'QA manager', hint: 'Scorecards, sampling, publishing, disputes; sees everyone' },
      { value: 'team_lead', label: 'Team lead', hint: 'Reviews, and sees scores for the teams they lead' },
      { value: 'agent', label: 'Agent', hint: 'Sees their own published reviews' },
    ],
  },
  academy: {
    key: 'academy',
    name: 'Academy',
    blurb: 'Training programs, the knowledge base and Ask.',
    roles: [
      { value: 'admin', label: 'Admin', hint: 'Everything, plus API tokens and settings' },
      { value: 'trainer', label: 'Trainer', hint: 'Writes programs and the knowledge base, grades, sees every learner' },
      { value: 'agent', label: 'Agent', hint: 'Their own training, the knowledge base and Ask' },
    ],
  },
};

export const isRoleFor = (app: AppKey, role: string) => APPS[app].roles.some((r) => r.value === role);
export const roleLabel = (app: AppKey, role: string) => APPS[app].roles.find((r) => r.value === role)?.label ?? role;

/** Who can manage people in the hub. `member` can sign in and open their apps. */
export const HubRole = z.enum(['admin', 'member']);
export type HubRole = z.infer<typeof HubRole>;

/**
 * What the hub sends an app about one person. `role: null` means "no access to this app": the
 * app keeps the person's row (history points at it) but marks it inactive.
 */
export const PersonPush = z.object({
  personId: z.string().min(1),
  email: z.string().email(),
  name: z.string().min(1),
  /** person.active AND has access to this app */
  active: z.boolean(),
  role: z.string().nullable(),
  startDate: z.string().nullable(),
});
export type PersonPush = z.infer<typeof PersonPush>;
